import React, { useCallback, useMemo, useState } from 'react'

import { AssetApproverFragment } from '~generated/operations'
import LikeIcon from '~icons/like.svg'
import SendIcon from '~icons/send.svg'
import Avatar from '~lib/ui-kit/Avatar'
import MenuLayout from '~lib/ui-kit/MenuLayout'
import MenuList from '~lib/ui-kit/MenuList/MenuList'
import Popover, { PopoverTrigger } from '~lib/ui-kit/Popover'
import ApproveButton from '~modules/assetModules/elements/ApproveButton'
import Button from '~shared/ui/Button'

export type ApproversMenuProps = {
  readOnly: boolean
  userId?: string
  approversDetails?: AssetApproverFragment[]
  onApprove?: VoidFunction
  onSendForApproval?: VoidFunction
}

const POPOVER_TRIGGER: PopoverTrigger[] = ['hover', 'focus']

function ApproversMenu({ approversDetails, readOnly, userId, onApprove, onSendForApproval }: ApproversMenuProps) {
  const [isOpened, setIsOpened] = useState(false)
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement>(null)

  const approver = approversDetails?.find((d) => d.userDetails.id === userId)
  const isCanApprove = !readOnly && approver && !approver?.hasApproved

  const approvedUsers = useMemo(
    () => approversDetails?.filter((item) => item.hasApproved).map((item) => item.userDetails),
    [approversDetails]
  )

  const waitingForApprovalUsers = useMemo(
    () => approversDetails?.filter((item) => !item.hasApproved).map((item) => item.userDetails),
    [approversDetails]
  )

  const handleApprove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      if (isCanApprove) {
        onApprove()
      }
    },
    [isCanApprove, onApprove]
  )

  const handleSendForApproval = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      setIsOpened(false)
      onSendForApproval()
    },
    [onSendForApproval]
  )

  const progress = approversDetails == null ? 0 : Math.round((approvedUsers.length / approversDetails.length) * 100)

  return (
    <>
      <ApproveButton
        ref={setButtonEl}
        icon={isCanApprove && !approver?.hasApproved && <LikeIcon />}
        onClick={handleApprove}
        variant={
          (isCanApprove && !approver?.hasApproved && 'highlight') ||
          ((isCanApprove || progress > 50) && 'approved') ||
          (progress > 0 && 'lavender') ||
          'secondary'
        }
      >
        {(isCanApprove && !approver?.hasApproved && 'Approve') ||
          (progress === 100 && 'Approved') ||
          `${progress}% Approved`}
      </ApproveButton>

      <Popover
        placement="top-start"
        width={201}
        anchorEl={buttonEl}
        open={isOpened}
        onChangeOpen={setIsOpened}
        trigger={POPOVER_TRIGGER}
      >
        {approversDetails && (
          <MenuLayout>
            <MenuList>
              {approvedUsers && approvedUsers.length > 0 && (
                <>
                  <MenuList.Subheading after={`${approvedUsers.length} out of ${approversDetails.length}`}>
                    Approved users
                  </MenuList.Subheading>

                  {approvedUsers.map((item) => (
                    <MenuList.Item
                      key={item.id}
                      icon={
                        <Avatar
                          alt={item.name || item.email}
                          color={item.avatarColor}
                          size="sm"
                          src={item.profileImage}
                        />
                      }
                    >
                      {item.name || item.email}
                    </MenuList.Item>
                  ))}
                </>
              )}

              {waitingForApprovalUsers && waitingForApprovalUsers.length > 0 && (
                <>
                  <MenuList.Subheading after={`${waitingForApprovalUsers.length} out of ${approversDetails.length}`}>
                    Waiting for approval
                  </MenuList.Subheading>

                  {waitingForApprovalUsers.map((item) => (
                    <MenuList.Item
                      key={item.id}
                      icon={
                        <Avatar
                          alt={item.name || item.email}
                          color={item.avatarColor}
                          size="sm"
                          src={item.profileImage}
                        />
                      }
                    >
                      {item.name || item.email}
                    </MenuList.Item>
                  ))}
                </>
              )}
            </MenuList>

            {!readOnly && (
              <MenuLayout.Footer>
                <Button before={<SendIcon />} variant="secondary" onClick={handleSendForApproval} className="p-0-25">
                  Send for review
                </Button>
              </MenuLayout.Footer>
            )}
          </MenuLayout>
        )}
      </Popover>
    </>
  )
}

export default ApproversMenu
